import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule } from "@angular/forms";
import { PreloadAllModules, RouterModule } from "@angular/router";
import "opensans-npm-webfont";
/*
 * Platform and Environment providers/directives/pipes
 */
import { environment } from "../environments/environment";
import { ROUTES } from "./app.routes";
// App is our top level component
import { AppComponent } from "./app.component";
import { APP_RESOLVER_PROVIDERS } from "./app.resolver";
import { AppState, IInternalStateType } from "./app.service";
import { NoContentComponent } from "./no-content";

import "../styles/styles.less";
import { AuthGuard } from "./guards/auth.guard";
import { AuthService } from "./services/auth.service";
import { RegistrationService } from "./services/registration.service";
import { AuthenticatedHttp } from "./services/http.service";
import { CustomDirectivesModule } from "./directives/customDirectives.module";
import { HttpResponseHandlerService } from "./services/httpResponseHandler.service";
import { BusyIconService } from "./services/busy.service";
import { UserService } from "./services/user.service";
import { FileService } from "./services/file.service";
import { LoginComponent } from "./common/loginViews/login";
import { HttpClientJsonpModule, HttpClientModule } from "@angular/common/http";
import { CgBusyModule } from "angular-busy2";
import { CommonModule } from "@angular/common";
import { ToastrModule } from "ngx-toastr";
import { LogoutComponent } from "./common/logout/logout.component";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { MailService } from "./services/mail.service";
import { CountryService } from "./services/country.service";
import { ExternalLoginComponent } from "./common/loginViews/externalLogin";
import { OidcClientService } from "./services/oidcClient.service";
import { PasswordStrengthBarModule } from "./helpers/passwordStrengthBar/passwordStrengthBar.module";
import { FooterModule } from "./common/footer/footer.module";
import { HeaderModule } from "./common/header/header.module";
import { ImprintModule } from "./common/imprint/imprint.module";
import { DynamicDialogModule } from "primeng/dynamicdialog";
import {
  L10nConfig, L10nLoader, L10nRoutingModule, L10nTranslationModule
} from "angular-l10n";
import { GlobalErrorHandler } from "./app.errorhandler";
import { DialogModule } from "primeng/dialog";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { MessagesModule } from "primeng/messages";
import { MessageModule } from "primeng/message";
import { HttpTranslationLoader } from "./services/http-translation.loader";
import { RegistrationComponent } from "./common/registration/registration.component";
import { NgSelectModule } from "@ng-select/ng-select";
import { RegistrationModule } from "./common/registration";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CustomPipesModule } from "./pipes/customPipes.module";
import { TooltipModule } from "primeng/tooltip";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { PanelModule } from "primeng/panel";
import { InputSwitchModule } from "primeng/inputswitch";
import { InputHelperModule } from "./helpers/inputDirectives/inputHelper.module";
import { DisclaimerModule } from "./common/disclaimer/disclaimer.module";
import { PasswordResetComponent } from "./common/loginViews/passwordReset/passwordReset.component";

// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS,
  UserService,
  AppState,
  AuthGuard,
  AuthService,
  CountryService,
  RegistrationService,
  AuthenticatedHttp,
  HttpResponseHandlerService,
  BusyIconService,
  MailService,
  FileService,
  OidcClientService,
  {
    provide: APP_INITIALIZER,
    useFactory: initL10n,
    deps: [L10nLoader],
    multi: true
  },
  {
    provide: ErrorHandler,
    useClass: GlobalErrorHandler
  }
];

interface IStoreType {
  state: IInternalStateType;
  restoreInputValues: () => void;
  disposeOldHosts: () => void;
}

const l10nConfig: L10nConfig = {
  format: "language",
  providers: [
    { name: "locale", asset: "./assets/resources/locale", options: { version: "11.0.0" } }
  ],
  fallback: false,
  cache: true,
  keySeparator: ".",
  defaultLocale: { language: "en" },
  schema: [
    { locale: { language: "en" }, dir: "ltr", text: "United States" },
    { locale: { language: "de" }, dir: "ltr", text: "Germany" }
  ],
  defaultRouting: true
};

// Advanced initialization.
export function initL10n(l10nLoader: L10nLoader): () => Promise<void> {
  return () => l10nLoader.init();
}

interface IStoreType {
  state: IInternalStateType;
  restoreInputValues: () => void;
  disposeOldHosts: () => void;
}

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    LoginComponent,
    ExternalLoginComponent,
    PasswordResetComponent,
    LogoutComponent,
    NoContentComponent
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    CgBusyModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    DialogModule,
    DisclaimerModule,
    ConfirmDialogModule,
    CustomDirectivesModule,
    CustomPipesModule,
    DynamicDialogModule,
    FontAwesomeModule,
    FooterModule,
    FormsModule,
    HeaderModule,
    HttpClientJsonpModule,
    HttpClientModule,
    ImprintModule,
    PasswordStrengthBarModule,
    MessageModule,
    MessagesModule,
    TooltipModule,
    OverlayPanelModule,
    PanelModule,
    NgSelectModule,
    InputHelperModule,
    InputSwitchModule,

    RouterModule.forRoot(ROUTES, {
      useHash: true,
      preloadingStrategy: PreloadAllModules,
      paramsInheritanceStrategy: "always"
    }),

    ToastrModule.forRoot({
      newestOnTop: true,
      positionClass: "toast-top-right",
      preventDuplicates: true,
      progressBar: true
    }),
    L10nRoutingModule.forRoot(),
    L10nTranslationModule,
    L10nTranslationModule.forRoot(
      l10nConfig,
      {
        translationLoader: HttpTranslationLoader
      }
    ),
    NgSelectModule,
    RegistrationModule,
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    environment.ENV_PROVIDERS,
    APP_PROVIDERS
  ]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas);
    library.addIconPacks(fab);
    library.addIconPacks(far);
  }
}
